import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';
import DeployIntakeForm from '../DeployIntakeForm';
import LyricConfirm from '../components/LyricConfirm';
import { formatBuild as fmtBuild } from '../utils/formatBuild';

/**
 * Admin → Deployed Apps page.
 *
 * Shows every app the server has registered, regardless of Okta visibility,
 * so admins can find orphaned/stale entries (e.g. "the URL responds but the
 * card shows Offline" caused by a dead pid still in the map).
 *
 * Backed by GET /api/admin/apps. Requires either:
 *   - email in LYRIC_ADMIN_EMAILS, or
 *   - membership in the Okta group 'lyric-admins'.
 */
export default function AdminAppsPage() {
  const { oktaAuth } = useOktaAuth() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState({}); // appName -> 'deleting' | 'stopping' | 'starting'
  const [diag, setDiag] = useState(null);
  const [diagError, setDiagError] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  // Per-row env-vars panel: which app is open, the fetched data, loading flag.
  // Keyed by appName so multiple rows can be open at once.
  const [envOpenFor, setEnvOpenFor] = useState(null);   // appName or null
  const [envData, setEnvData] = useState({});           // { [appName]: response }
  const [envBusy, setEnvBusy] = useState({});           // { [appName]: bool }
  // Deploy-log panel: which app's version history is expanded. The history is
  // already included in the /api/admin/apps response (a.deployHistory), so this
  // is a pure UI toggle — no extra fetch.
  const [logOpenFor, setLogOpenFor] = useState(null);   // appName or null
  // Inline display-name rename. renameFor = appName currently being edited,
  // renameDraft holds the in-progress text, renameBusy = save in flight.
  const [renameFor, setRenameFor] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  // "Create iOS app from this app": iosBusy is the app being analysed, iosSpec
  // holds the analysis result that seeds the iOS build wizard.
  const [iosBusy, setIosBusy] = useState(null);   // appName or null
  const [iosSpec, setIosSpec] = useState(null);   // analysis response or null
  const [iosError, setIosError] = useState('');
  // Admin edit-config panel: which app is open, the in-progress draft (keyed
  // form fields), the save-in-flight flag, and the last result message.
  const [editOpenFor, setEditOpenFor] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState(null); // { type: 'ok'|'warn'|'err', text }
  // All question-popups route through one Lyric dialog (never window.confirm).
  // confirm = { kind: 'delete' | 'redeploy' | 'quarantine', appName } | null
  const [confirm, setConfirm] = useState(null);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch('/api/admin/apps', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const hint = body?.hint ? ` (${body.hint})` : '';
        throw new Error(`${body?.error || `HTTP ${res.status}`}${hint}`);
      }
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [oktaAuth]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // "📱 iOS app": read the deployed app's repo, work out what it does and which
  // endpoints it exposes, then open the iOS (SwiftUI) build wizard prefilled
  // with that spec. The wizard does the actual generation.
  const handleCreateIosApp = useCallback(async (appName) => {
    setIosBusy(appName);
    setIosError('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      // Private repos need the caller's PAT, same as the build/enhance flows.
      let ghToken = '';
      try { ghToken = localStorage.getItem('lyric-build-github-token') || ''; } catch { /* ignore */ }

      const res = await fetch('/api/deploy/ios-from-app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(ghToken ? { 'X-User-GitHub-Token': ghToken } : {}),
        },
        body: JSON.stringify({ appName }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setIosSpec(body);
    } catch (e) {
      setIosError(`Could not analyse ${appName}: ${e.message}`);
    } finally {
      setIosBusy(null);
    }
  }, [oktaAuth]);

  // Pod self-diagnostics: state file, child processes, listening ports, env.
  // Replaces the need for `kubectl exec` when debugging missing-app issues.
  const fetchDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    setDiagError('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch('/api/admin/diagnostics', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setDiag(body);
    } catch (e) {
      setDiagError(e.message);
    } finally {
      setDiagLoading(false);
    }
  }, [oktaAuth]);

  // Auto-fetch the first time the panel is opened.
  useEffect(() => {
    if (diagOpen && !diag && !diagLoading) fetchDiagnostics();
  }, [diagOpen, diag, diagLoading, fetchDiagnostics]);

  // Admin Stop & Remove: hits DELETE /api/admin/apps/:appName which kills the
  // process (by pid or port) and deletes the server-state entry. No ownership
  // check — that's the whole point of an admin action.
  const handleAdminDelete = useCallback((appName) => {
    // Opens the Lyric type-the-name confirmation; doAdminDelete does the work.
    setConfirm({ kind: 'delete', appName });
  }, []);

  const doAdminDelete = useCallback(async (appName) => {
    setBusy(prev => ({ ...prev, [appName]: 'deleting' }));
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // Server requires the typed name as defense-in-depth (CONFIRM_NAME_MISMATCH).
        body: JSON.stringify({ confirmName: appName }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      // Optimistically drop the row, then refresh to confirm.
      setData(prev => prev ? { ...prev, apps: prev.apps.filter(a => a.appName !== appName), total: Math.max(0, prev.total - 1) } : prev);
      fetchApps();
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  }, [oktaAuth, fetchApps]);

  // Stop ↔ Start toggle. Stop kills the process but keeps the registry entry
  // so config (owner, secrets, groups, start command) is preserved. Start
  // re-clones + re-installs + spawns from that same entry.
  const handleStopStart = useCallback(async (app) => {
    const isRunning = app.status === 'running' || app.status === 'deploying';
    const action = isRunning ? 'stop' : 'start';
    setBusy(prev => ({ ...prev, [app.appName]: action === 'stop' ? 'stopping' : 'starting' }));
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(app.appName)}/${action}`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      // Start kicks off a 30s–3min rebuild; refresh now and let the user
      // hit Refresh again to see it flip to running.
      fetchApps();
    } catch (e) {
      setError(`${action} failed: ${e.message}`);
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[app.appName]; return n; });
    }
  }, [oktaAuth, fetchApps]);

  // Quarantine: the site-saver. Kills the process AND blocks all auto-redeploy
  // (including on pod restart) until an admin clicks Start, so a crash-looping
  // app can't keep taking the shared pod down. Config + secrets are preserved.
  // POST /api/admin/apps/:appName/quarantine.
  const handleQuarantine = useCallback((appName) => {
    setConfirm({ kind: 'quarantine', appName });
  }, []);

  const doQuarantine = useCallback(async (appName) => {
    setBusy(prev => ({ ...prev, [appName]: 'quarantining' }));
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/quarantine`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      fetchApps();
    } catch (e) {
      setError(`Quarantine failed: ${e.message}`);
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  }, [oktaAuth, fetchApps]);

  // Lift quarantine WITHOUT redeploying — clears the flag so the app can be
  // started later ("clear now, start later"). Start also clears quarantine,
  // so use this only when you want the flag gone but the app left stopped.
  // POST /api/admin/apps/:appName/unquarantine.
  const handleUnquarantine = useCallback(async (appName) => {
    setBusy(prev => ({ ...prev, [appName]: 'unquarantining' }));
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/unquarantine`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      fetchApps();
    } catch (e) {
      setError(`Lift quarantine failed: ${e.message}`);
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  }, [oktaAuth, fetchApps]);

  // Persist a new display name via PATCH /api/admin/apps/:appName/display-name.
  // The URL slug and underlying process don't change — this is a label-only
  // rename. Closes the inline editor on success.
  const saveRename = useCallback(async (appName) => {
    const next = renameDraft.trim();
    if (!next) {
      setRenameFor(null);
      return;
    }
    setRenameBusy(true);
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/display-name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ displayName: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      // Optimistic update so the user sees the new name without a full refetch.
      setData(prev => prev ? {
        ...prev,
        apps: prev.apps.map(a => a.appName === appName ? { ...a, displayName: next } : a),
      } : prev);
      setRenameFor(null);
      setRenameDraft('');
    } catch (e) {
      setError(`Rename failed: ${e.message}`);
    } finally {
      setRenameBusy(false);
    }
  }, [oktaAuth, renameDraft]);

  // Fetch + cache env-vars view for a single app. Toggles the inline panel.
  // Result is cached per appName so re-opening the same row is instant; click
  // 'Refresh env' inside the panel to force a re-fetch.
  const handleEnvToggle = useCallback(async (appName, force = false) => {
    if (envOpenFor === appName && !force) {
      setEnvOpenFor(null);
      return;
    }
    setEnvOpenFor(appName);
    if (envData[appName] && !force) return;  // cached
    setEnvBusy(prev => ({ ...prev, [appName]: true }));
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/env`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setEnvData(prev => ({ ...prev, [appName]: body }));
    } catch (e) {
      setEnvData(prev => ({ ...prev, [appName]: { _error: e.message } }));
    } finally {
      setEnvBusy(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  }, [oktaAuth, envOpenFor, envData]);

  // Open (or toggle closed) the admin edit-config panel for an app, seeding the
  // draft from the app's current values returned by /api/admin/apps.
  const openEdit = useCallback((app) => {
    if (editOpenFor === app.appName) { setEditOpenFor(null); return; }
    setEditMsg(null);
    setEditDraft({
      forwardOktaToken: !!app.forwardOktaToken,
      ownerOnly: !!app.ownerOnly,
      allowedGroups: (app.allowedGroups || []).join(', '),
      coAdmins: (app.coAdmins || []).join(', '),
      secretName: app.secretName || '',
      branch: app.branch || '',
      startCommand: app.startCommand || '',
      buildCommand: app.buildCommand || '',
      subdir: app.subdir || '',
    });
    setEditOpenFor(app.appName);
  }, [editOpenFor]);

  const saveConfig = useCallback(async (appName) => {
    setEditSaving(true);
    setEditMsg(null);
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const d = editDraft;
      const payload = {
        forwardOktaToken: !!d.forwardOktaToken,
        ownerOnly: !!d.ownerOnly,
        allowedGroups: String(d.allowedGroups || '').split(',').map(s => s.trim()).filter(Boolean),
        coAdmins: String(d.coAdmins || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
        secretName: String(d.secretName || '').trim() || null,
        branch: String(d.branch || '').trim(),
        startCommand: String(d.startCommand || ''),
        buildCommand: String(d.buildCommand || ''),
        subdir: String(d.subdir || ''),
      };
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || (body?.details ? body.details.join('; ') : `HTTP ${res.status}`));
      const live = body.appliedLive || [];
      const needs = body.needsRedeploy || [];
      let text = '✅ Saved.';
      if (live.length) text += ` Applied now: ${live.join(', ')}.`;
      if (needs.length) text += ` Redeploy to apply: ${needs.join(', ')}.`;
      if (body.secretResolves === false) text += ' ⚠️ That secret does NOT resolve in AWS Secrets Manager!';
      setEditMsg({ type: body.secretResolves === false ? 'err' : needs.length ? 'warn' : 'ok', text });
      fetchApps();
    } catch (e) {
      setEditMsg({ type: 'err', text: e.message });
    } finally {
      setEditSaving(false);
    }
  }, [oktaAuth, editDraft, fetchApps]);

  const handleRedeploy = useCallback((appName) => {
    setConfirm({ kind: 'redeploy', appName });
  }, []);

  const doRedeploy = useCallback(async (appName) => {
    setBusy(prev => ({ ...prev, [appName]: 'redeploying' }));
    setEditMsg(null);
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(appName)}/redeploy`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setEditMsg({ type: 'ok', text: `🚀 Redeploy started for ${appName} — status will update as it rebuilds.` });
      setTimeout(fetchApps, 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(prev => { const n = { ...prev }; delete n[appName]; return n; });
    }
  }, [oktaAuth, fetchApps]);

  // Dispatches the open Lyric confirmation to the matching action.
  const runConfirm = useCallback(() => {
    if (!confirm) return;
    const { kind, appName } = confirm;
    setConfirm(null);
    if (kind === 'delete') doAdminDelete(appName);
    else if (kind === 'redeploy') doRedeploy(appName);
    else if (kind === 'quarantine') doQuarantine(appName);
  }, [confirm, doAdminDelete, doRedeploy, doQuarantine]);

  // Props for the shared Lyric confirmation, derived from the pending action.
  const confirmProps = (() => {
    if (!confirm) return { isOpen: false };
    const { kind, appName } = confirm;
    if (kind === 'delete') {
      return {
        isOpen: true,
        tone: 'danger',
        title: 'Delete this app from the server?',
        confirmLabel: 'Delete this app',
        requireText: appName,
        requireLabel: (<>Type <code>{appName}</code> to confirm deletion</>),
        requirePlaceholder: appName,
        message: (
          <>
            This permanently removes <strong>{appName}</strong> from the server: it
            stops the process and deletes the persisted entry. It does <strong>not</strong> touch
            AWS Secrets Manager values or the source repo. The app’s managers will be emailed.
          </>
        ),
      };
    }
    if (kind === 'redeploy') {
      return {
        isOpen: true,
        title: `Redeploy ${appName}?`,
        confirmLabel: 'Redeploy',
        message: `This stops the running app and rebuilds it from its repo with the current config. The app’s managers will be emailed.`,
      };
    }
    if (kind === 'quarantine') {
      return {
        isOpen: true,
        tone: 'danger',
        title: `Quarantine ${appName}?`,
        confirmLabel: 'Quarantine',
        message: `This KILLS the process and BLOCKS all auto-redeploy (even across pod restarts) until an admin clicks Start. Use it to stop a crash-looping app from taking the site down. Config + secrets are preserved.`,
      };
    }
    return { isOpen: false };
  })();

  const apps = (data?.apps || []).filter(a => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return a.appName.toLowerCase().includes(q)
      || (a.displayName || '').toLowerCase().includes(q)
      || (a.deployedBy || '').toLowerCase().includes(q)
      || (a.repo || '').toLowerCase().includes(q);
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>🛠 Admin · Deployed Apps</h1>
        {/* Page controls + admin nav share a single row so nothing overlaps
            and the alignment matches across admin pages. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="search"
            placeholder="Filter by name, repo, owner…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.search}
          />
          <button onClick={fetchApps} style={styles.refreshBtn} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
          <AdminMenu />
        </div>
      </div>

      {error && <div style={styles.error}>⚠️ {error}</div>}

      <LyricConfirm
        {...confirmProps}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />

      {data && (
        <div style={styles.summary}>
          Signed in as <strong>{data.currentUser || 'unknown'}</strong>
          {' · '}{apps.length} of {data.total} apps shown
        </div>
      )}

      <details
        open={diagOpen}
        onToggle={(e) => setDiagOpen(e.currentTarget.open)}
        style={styles.diagBox}
      >
        <summary style={styles.diagSummary}>
          🩺 Pod Diagnostics
          <span style={{ color: '#888', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
            (state file, child processes, listening ports, env)
          </span>
        </summary>
        <div style={{ padding: '12px 4px' }}>
          <button
            onClick={fetchDiagnostics}
            disabled={diagLoading}
            style={{ ...styles.refreshBtn, marginBottom: 8 }}
          >
            {diagLoading ? '…' : '↻ Refresh diagnostics'}
          </button>
          {diagError && <div style={styles.error}>⚠️ {diagError}</div>}
          {diag && (
            <div style={{ display: 'grid', gap: 12 }}>
              {diag.stateBackend && (
                <div style={{
                  padding: '10px 12px',
                  borderRadius: 6,
                  background: diag.stateBackend.kind === 's3' ? '#1b3a1f' : '#3a2a1b',
                  border: `1px solid ${diag.stateBackend.kind === 's3' ? '#4caf50' : '#ff9800'}`,
                  fontSize: 13,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    State backend: {diag.stateBackend.kind === 's3' ? '✅ S3 (persistent)' : '⚠️ file (ephemeral)'}
                  </div>
                  <code style={{ fontSize: 11, color: '#ccc' }}>{diag.stateBackend.description}</code>
                </div>
              )}
              {diag.secretRotation && (
                <SecretRotationPanel data={diag.secretRotation} />
              )}
              <DiagSection title="Host" obj={diag.host} />
              <DiagSection title="Env" obj={diag.env} />
              <DiagSection
                title={`In-Memory Apps (${diag.inMemoryAppCount})`}
                obj={diag.inMemoryApps}
              />
              <DiagSection
                title={`State File · ${diag.stateFile?.path || '?'} · ${diag.stateFile?.sizeBytes ?? '?'} bytes · mtime ${diag.stateFile?.mtime || '?'}`}
                raw={diag.stateFile?.contents}
              />
              <DiagSection title="Child Processes (ps -ef | grep node/python/...)" raw={diag.processes} />
              <DiagSection title="Listening Ports (ss / netstat / lsof)" raw={diag.listeningPorts} />
              <div style={{ color: '#666', fontSize: 11 }}>
                Collected at {diag.collectedAt}
              </div>
            </div>
          )}
        </div>
      </details>

      {data && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>App</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>PID</th>
              <th style={styles.th}>Port</th>
              <th style={styles.th}>Owner</th>
              <th style={styles.th}>Repo · subdir</th>
              <th style={styles.th}>Groups</th>
              <th style={styles.th}>URL</th>
              <th style={styles.th}>Error</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {apps.length === 0 && (
              <tr><td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#888' }}>
                No apps {filter ? 'match the filter' : 'deployed'}.
              </td></tr>
            )}
            {apps.map((a) => {
              const statusColor = a.status === 'running' ? '#4caf50'
                : a.status === 'error' ? '#ef5350'
                : a.status === 'deploying' ? '#4fc3f7'
                : '#888';
              const pidWarn = a.pid && !a.pidAlive;
              return (
                <React.Fragment key={a.appName}>
                <tr>
                  <td style={styles.td}>
                    {renameFor === a.appName ? (
                      // Inline rename editor. Enter saves, Esc cancels.
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="text"
                          autoFocus
                          value={renameDraft}
                          maxLength={80}
                          disabled={renameBusy}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(a.appName);
                            else if (e.key === 'Escape') { setRenameFor(null); setRenameDraft(''); }
                          }}
                          style={{
                            ...styles.search,
                            minWidth: 200, fontSize: 12,
                            padding: '4px 8px',
                          }}
                        />
                        <button
                          onClick={() => saveRename(a.appName)}
                          disabled={renameBusy || !renameDraft.trim()}
                          style={{
                            padding: '4px 8px', fontSize: 11, fontWeight: 600,
                            background: '#0e3a2a', color: '#81c784',
                            border: '1px solid #81c784', borderRadius: 4,
                            cursor: renameBusy ? 'wait' : 'pointer',
                          }}
                          title="Save (Enter)"
                        >
                          {renameBusy ? '⏳' : '✓'}
                        </button>
                        <button
                          onClick={() => { setRenameFor(null); setRenameDraft(''); }}
                          disabled={renameBusy}
                          style={{
                            padding: '4px 8px', fontSize: 11, fontWeight: 600,
                            background: '#1a1f2a', color: '#bbb',
                            border: '1px solid #555', borderRadius: 4, cursor: 'pointer',
                          }}
                          title="Cancel (Esc)"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ fontWeight: 600 }}>{a.displayName || a.appName}</div>
                        <button
                          onClick={() => { setRenameFor(a.appName); setRenameDraft(a.displayName || a.appName); }}
                          style={{
                            padding: 0, background: 'transparent', border: 'none',
                            color: '#666', cursor: 'pointer', fontSize: 12, lineHeight: 1,
                          }}
                          title="Rename display label (URL slug stays the same)"
                        >
                          ✎
                        </button>
                      </div>
                    )}
                    <code style={{ fontSize: 11, color: '#888' }}>{a.appName}</code>
                    {a.version && (
                      <span style={styles.versionTag} title={`Current deploy version — ${(a.deployHistory || []).length} deploy(s) logged`}>
                        v{a.version}
                      </span>
                    )}
                    {fmtBuild(a.buildNumber) && (
                      <span style={styles.buildTag} title={`Current build number`}>
                        build {fmtBuild(a.buildNumber)}
                      </span>
                    )}
                    {/* Safety badges: quarantine state + pre-deploy env findings.
                        These surface why an app isn't auto-recovering at a glance. */}
                    {(a.quarantined || (a.envWarnings && a.envWarnings.length > 0) || a.autoRedeployFailures > 0) && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                        {a.quarantined && (
                          <span
                            title={a.quarantinedReason || 'Auto-redeploy disabled until a manual Start'}
                            style={styles.badgeQuarantined}
                          >
                            🚫 Quarantined
                          </span>
                        )}
                        {!a.quarantined && a.autoRedeployFailures > 0 && (
                          <span
                            title={`${a.autoRedeployFailures} consecutive auto-redeploy attempt(s) that didn't stay up. Quarantines automatically at the limit.`}
                            style={styles.badgeFailures}
                          >
                            ↻ {a.autoRedeployFailures} fail{a.autoRedeployFailures === 1 ? '' : 's'}
                          </span>
                        )}
                        {a.envWarnings && a.envWarnings.length > 0 && (() => {
                          const errs = a.envWarnings.filter(w => w.severity === 'error').length;
                          const warns = a.envWarnings.length - errs;
                          const tip = a.envWarnings
                            .map(w => `[${w.severity}] ${w.key}: ${w.message}`)
                            .join('\n');
                          return (
                            <span
                              title={tip}
                              style={errs > 0 ? styles.badgeEnvError : styles.badgeEnvWarn}
                            >
                              {errs > 0 ? '⛔' : '⚠️'} env: {errs > 0 ? `${errs} err` : ''}{errs > 0 && warns > 0 ? ', ' : ''}{warns > 0 ? `${warns} warn` : ''}
                            </span>
                          );
                        })()}
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, color: statusColor, fontWeight: 600 }}>
                    {a.status}
                    {a.stage && <div style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>{a.stage}</div>}
                  </td>
                  <td style={{ ...styles.td, color: pidWarn ? '#ef5350' : '#ccc' }}>
                    {a.pid || '—'}
                    {pidWarn && <div style={{ fontSize: 10 }}>⚠ dead</div>}
                  </td>
                  <td style={styles.td}>{a.port}</td>
                  <td style={styles.td}>
                    {a.deployedBy}
                    {Array.isArray(a.coAdmins) && a.coAdmins.length > 0 && (
                      <div style={{ color: '#ce93d8', fontSize: 10 }} title={a.coAdmins.join(', ')}>
                        +{a.coAdmins.length} co-admin{a.coAdmins.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ ...styles.td, fontSize: 11 }}>
                    <div>{a.owner}/{a.repo}</div>
                    <div style={{ color: '#888' }}>{a.branch}{a.subdir ? ` · ${a.subdir}` : ''}</div>
                  </td>
                  <td style={{ ...styles.td, fontSize: 11 }}>
                    {a.allowedGroups.length === 0
                      ? <em style={{ color: '#888' }}>everyone</em>
                      : a.allowedGroups.join(', ')}
                  </td>
                  <td style={{ ...styles.td, fontSize: 11 }}>
                    {a.url ? <a href={a.url} target="_blank" rel="noreferrer" style={{ color: '#4fc3f7' }}>open ↗</a> : '—'}
                  </td>
                  <td style={{ ...styles.td, fontSize: 11, color: '#ef9a9a', maxWidth: 240, wordBreak: 'break-word' }}>
                    {a.error || ''}
                  </td>
                  <td style={styles.td}>
                    {(() => {
                      const isRunning = a.status === 'running' || a.status === 'deploying';
                      const busyState = busy[a.appName];
                      const toggleLabel = busyState === 'stopping' ? '⏳ Stopping…'
                        : busyState === 'starting' ? '⏳ Starting…'
                        : isRunning ? '⏹ Stop' : '▶ Start';
                      const toggleColor = isRunning ? '#ffb74d' : '#66bb6a';
                      const envOpen = envOpenFor === a.appName;
                      const editOpen = editOpenFor === a.appName;
                      const logOpen = logOpenFor === a.appName;
                      const envLoading = !!envBusy[a.appName];
                      return (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleStopStart(a)}
                            disabled={!!busyState}
                            title={isRunning
                              ? 'Kill the process but keep the registry entry (config preserved)'
                              : 'Re-clone, re-install, and re-spawn from the persisted entry'}
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: busyState ? '#333' : '#1a1f2a',
                              color: busyState ? '#666' : toggleColor,
                              border: `1px solid ${busyState ? '#444' : toggleColor}`,
                              borderRadius: 4, cursor: busyState ? 'not-allowed' : 'pointer',
                              minWidth: 70,
                            }}
                          >
                            {toggleLabel}
                          </button>
                          <button
                            onClick={() => handleRedeploy(a.appName)}
                            disabled={!!busyState}
                            title="Stop, rebuild from the repo, and respawn with the current config (applies branch/secret/command changes)"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: busyState ? '#333' : '#1a1f2a',
                              color: busyState ? '#666' : '#4fc3f7',
                              border: `1px solid ${busyState ? '#444' : '#4fc3f7'}`,
                              borderRadius: 4, cursor: busyState ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {busyState === 'redeploying' ? '⏳ Redeploying…' : '🚀 Redeploy'}
                          </button>
                          {/* Quarantine ↔ Lift. The site-saver action: blocks all
                              auto-redeploy until a manual Start. When already
                              quarantined, offer "Lift" (clear flag, leave stopped). */}
                          {a.quarantined ? (
                            <button
                              onClick={() => handleUnquarantine(a.appName)}
                              disabled={!!busyState}
                              title="Clear the quarantine flag without redeploying (Start also clears it)"
                              style={{
                                padding: '4px 10px', fontSize: 11, fontWeight: 600,
                                backgroundColor: busyState ? '#333' : '#1a1f2a',
                                color: busyState ? '#666' : '#ba68c8',
                                border: `1px solid ${busyState ? '#444' : '#ba68c8'}`,
                                borderRadius: 4, cursor: busyState ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {busyState === 'unquarantining' ? '⏳ Lifting…' : '🔓 Lift quarantine'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleQuarantine(a.appName)}
                              disabled={!!busyState}
                              title="Kill the process and block ALL auto-redeploy until a manual Start — stops a crash-looping app from taking the site down"
                              style={{
                                padding: '4px 10px', fontSize: 11, fontWeight: 600,
                                backgroundColor: busyState ? '#333' : '#2a1f2a',
                                color: busyState ? '#666' : '#ce93d8',
                                border: `1px solid ${busyState ? '#444' : '#ce93d8'}`,
                                borderRadius: 4, cursor: busyState ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {busyState === 'quarantining' ? '⏳ Quarantining…' : '🚫 Quarantine'}
                            </button>
                          )}
                          <button
                            onClick={() => handleEnvToggle(a.appName)}
                            disabled={envLoading}
                            title="Show the AWS secret keys and (on Linux) live process env for this app"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: envOpen ? '#162132' : '#1a1f2a',
                              color: '#4fc3f7',
                              border: '1px solid #4fc3f7',
                              borderRadius: 4, cursor: envLoading ? 'wait' : 'pointer',
                            }}
                          >
                            {envLoading ? '⏳' : '🔑'} Env {envOpen ? '▴' : '▾'}
                          </button>
                          <button
                            onClick={() => setLogOpenFor(prev => prev === a.appName ? null : a.appName)}
                            title="Show the deploy log — every version deployed for this app, with who/when"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: logOpen ? '#1a2620' : '#1a1f2a',
                              color: '#80cbc4',
                              border: '1px solid #80cbc4',
                              borderRadius: 4, cursor: 'pointer',
                            }}
                          >
                            🕘 Log {logOpen ? '▴' : '▾'}
                          </button>
                          <button
                            onClick={() => openEdit(a)}
                            title="Edit this app's run config (Okta forwarding, groups, secret, branch, commands) — admin override, any owner"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: editOpen ? '#25202f' : '#1a1f2a',
                              color: '#ce93d8',
                              border: '1px solid #ce93d8',
                              borderRadius: 4, cursor: 'pointer',
                            }}
                          >
                            ✏️ Edit {editOpen ? '▴' : '▾'}
                          </button>
                          <button
                            onClick={() => handleCreateIosApp(a.appName)}
                            disabled={!!iosBusy}
                            title="Read this app's repo, work out what it does, and start an iOS (SwiftUI) build prefilled with that spec"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: iosBusy ? '#333' : '#1a1f2a',
                              color: iosBusy ? '#666' : '#a5d6a7',
                              border: `1px solid ${iosBusy ? '#444' : '#a5d6a7'}`,
                              borderRadius: 4, cursor: iosBusy ? 'wait' : 'pointer',
                            }}
                          >
                            {iosBusy === a.appName ? '⏳ Analysing…' : '📱 Create iOS app'}
                          </button>
                          <button
                            onClick={() => handleAdminDelete(a.appName)}
                            disabled={!!busyState}
                            title="Stop AND delete this app from server state (cannot be undone)"
                            style={{
                              padding: '4px 10px', fontSize: 11, fontWeight: 600,
                              backgroundColor: busyState ? '#333' : '#2a1a1a',
                              color: busyState ? '#666' : '#ef5350',
                              border: `1px solid ${busyState ? '#444' : '#ef5350'}`,
                              borderRadius: 4, cursor: busyState ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {busyState === 'deleting' ? '⏳ Removing…' : '🗑 Remove'}
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                {/* Env-vars panel — full-width row inserted below the app row when toggled open. */}
                {envOpenFor === a.appName && (
                  <tr>
                    <td colSpan={10} style={{ ...styles.td, background: '#0c1018', padding: 16 }}>
                      <EnvPanel
                        env={envData[a.appName]}
                        loading={envBusy[a.appName]}
                        onRefresh={() => handleEnvToggle(a.appName, true)}
                      />
                    </td>
                  </tr>
                )}
                {/* Deploy-log panel — every version deployed for this app. */}
                {logOpenFor === a.appName && (
                  <tr>
                    <td colSpan={10} style={{ ...styles.td, background: '#0c1018', padding: 16 }}>
                      <DeployLogPanel app={a} oktaAuth={oktaAuth} />
                    </td>
                  </tr>
                )}
                {/* Admin edit-config panel — full-width row for editing run config. */}
                {editOpenFor === a.appName && (
                  <tr>
                    <td colSpan={10} style={{ ...styles.td, background: '#0c1018', padding: 16 }}>
                      <EditConfigPanel
                        app={a}
                        draft={editDraft}
                        setDraft={setEditDraft}
                        saving={editSaving}
                        msg={editMsg}
                        onSave={() => saveConfig(a.appName)}
                        onRedeploy={() => handleRedeploy(a.appName)}
                        onClose={() => setEditOpenFor(null)}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {iosError && (
        <div style={{
          marginTop: 16, padding: '10px 14px', borderRadius: 6,
          backgroundColor: '#2a1a1a', border: '1px solid #ef5350', color: '#ef9a9a', fontSize: 13,
        }}>
          {iosError}
        </div>
      )}

      {iosSpec && (
        <DeployIntakeForm
          initialIosFromApp={iosSpec}
          onClose={() => { setIosSpec(null); fetchApps(); }}
        />
      )}
    </div>
  );
}

const styles = {
  page: { padding: 24, color: '#eee', backgroundColor: '#0f0f17', minHeight: '100vh', fontFamily: 'ui-sans-serif, system-ui' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 },
  title: { margin: 0, fontSize: 20, color: '#ffb300' },
  search: { backgroundColor: '#1e1e2e', border: '1px solid #444', borderRadius: 4, color: '#eee', padding: '6px 10px', fontSize: 13, minWidth: 260 },
  refreshBtn: { padding: '6px 12px', fontSize: 12, border: '1px solid #555', borderRadius: 4, backgroundColor: '#2a2a3a', color: '#eee', cursor: 'pointer' },
  summary: { color: '#aaa', fontSize: 12, marginBottom: 12 },
  error: { backgroundColor: '#3a1a1a', color: '#ef9a9a', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12, backgroundColor: '#15151f', borderRadius: 6, overflow: 'hidden' },
  th: { textAlign: 'left', padding: '10px 8px', borderBottom: '2px solid #333', color: '#aaa', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, backgroundColor: '#1a1a26' },
  td: { padding: '8px', borderBottom: '1px solid #222', verticalAlign: 'top' },
  // Safety badges shown under the app name. Shared pill base + per-severity color.
  badgeQuarantined: { display: 'inline-block', padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#3a1a2a', color: '#ce93d8', border: '1px solid #ce93d8', whiteSpace: 'nowrap' },
  badgeFailures: { display: 'inline-block', padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#3a2a1b', color: '#ffb74d', border: '1px solid #ffb74d', whiteSpace: 'nowrap', cursor: 'help' },
  badgeEnvError: { display: 'inline-block', padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#3a1a1a', color: '#ef5350', border: '1px solid #ef5350', whiteSpace: 'nowrap', cursor: 'help' },
  badgeEnvWarn: { display: 'inline-block', padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#3a3320', color: '#ffd54f', border: '1px solid #ffd54f', whiteSpace: 'nowrap', cursor: 'help' },
  // Current deploy version pill, shown beside the app slug.
  versionTag: { display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#12251f', color: '#80cbc4', border: '1px solid #2e7d6b', whiteSpace: 'nowrap', cursor: 'help', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace' },
  buildTag: { display: 'inline-block', marginLeft: 6, padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: '#1c1c28', color: '#8a97a8', border: '1px solid #3a3a4c', whiteSpace: 'nowrap', cursor: 'help', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', textTransform: 'uppercase' },
  diagBox: { backgroundColor: '#15151f', border: '1px solid #2a2a3a', borderRadius: 6, padding: '10px 14px', marginBottom: 16 },
  diagSummary: { cursor: 'pointer', color: '#ffb300', fontWeight: 600, fontSize: 13, outline: 'none' },
  diagSectionTitle: { color: '#aaa', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
  diagPre: { margin: 0, padding: 10, backgroundColor: '#0b0b12', border: '1px solid #2a2a3a', borderRadius: 4, fontSize: 11, color: '#cfd8dc', overflowX: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  diagCellHeader: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #333', color: '#aaa', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 },
  diagCellBody: { padding: '6px 8px', borderBottom: '1px solid #222', verticalAlign: 'top', color: '#cfd8dc', fontSize: 11 },
};

// Renders either a JSON-formatted object or raw text inside a scrollable <pre>.
function DiagSection({ title, obj, raw }) {
  let body;
  if (obj !== undefined) body = JSON.stringify(obj, null, 2);
  else if (raw !== undefined && raw !== null) body = String(raw);
  else body = '(empty)';
  return (
    <div>
      <div style={styles.diagSectionTitle}>{title}</div>
      <pre style={styles.diagPre}>{body}</pre>
    </div>
  );
}

// Renders the secret-rotation watcher status from /api/admin/diagnostics.
// Each watcher row says which app, what AWS secret it's watching, when the
// last poll succeeded, and how many rotations it has detected. A row with
// `lastError` flips the row red so it's obvious at a glance.
function SecretRotationPanel({ data }) {
  const interval = data?.pollIntervalSecs ?? 0;
  const watchers = Array.isArray(data?.watchers) ? data.watchers : [];
  const disabled = interval === 0;
  const headerBg = disabled ? '#3a2a1b' : '#1b2a3a';
  const headerBorder = disabled ? '#ff9800' : '#4fc3f7';
  return (
    <div style={{
      padding: '10px 12px',
      borderRadius: 6,
      background: headerBg,
      border: `1px solid ${headerBorder}`,
      fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        🔑 Secret-rotation watchers
        <span style={{ color: '#ccc', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>
          {disabled
            ? 'disabled (LYRIC_SECRET_POLL_SECS=0)'
            : `polling every ${interval}s · ${watchers.length} active`}
        </span>
      </div>
      {!disabled && watchers.length === 0 && (
        <div style={{ color: '#bbb', fontSize: 12, fontStyle: 'italic' }}>
          No watchers attached. Apps must be deployed with the "Use Lyric AWS Secret"
          option (and that secret must exist under <code>tools/lyric/*</code>) before
          a watcher will start.
        </div>
      )}
      {watchers.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 4 }}>
          <thead>
            <tr>
              <th style={styles.diagCellHeader}>App / Secret</th>
              <th style={styles.diagCellHeader}>Baseline</th>
              <th style={styles.diagCellHeader}>Last Poll</th>
              <th style={styles.diagCellHeader}>Rotations</th>
              <th style={styles.diagCellHeader}>Last Error</th>
            </tr>
          </thead>
          <tbody>
            {watchers.map(w => (
              <tr key={w.appName} style={w.lastError ? { background: '#2a1a1a' } : undefined}>
                <td style={styles.diagCellBody}>
                  <div style={{ fontWeight: 600, color: '#e0e0e0' }}>{w.appName}</div>
                  <code style={{ color: '#888', fontSize: 10 }}>{w.secretId}</code>
                </td>
                <td style={styles.diagCellBody}>{w.baseline || '—'}</td>
                <td style={styles.diagCellBody}>{w.lastPoll || '—'}</td>
                <td style={{
                  ...styles.diagCellBody,
                  color: w.rotations > 0 ? '#81c784' : '#999',
                  fontWeight: w.rotations > 0 ? 600 : 400,
                }}>
                  {w.rotations}
                </td>
                <td style={{ ...styles.diagCellBody, color: '#ef9a9a', fontSize: 10 }}>
                  {w.lastError ? `${w.lastError} (at ${w.lastErrorAt})` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Admin edit-config panel. Renders a small form for an app's run-critical
// fields and posts changes to PATCH /api/admin/apps/:appName/config. Live
// fields (Okta forwarding, groups) apply immediately; the rest need a redeploy,
// which the "🚀 Redeploy now" button triggers via /redeploy.
function EditConfigPanel({ app, draft, setDraft, saving, msg, onSave, onRedeploy, onClose }) {
  const set = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));
  const labelStyle = { display: 'block', fontSize: 11, color: '#aaa', marginBottom: 3, fontWeight: 600 };
  const inputStyle = { width: '100%', boxSizing: 'border-box', background: '#0b0b12', border: '1px solid #2a2a3a', borderRadius: 4, color: '#eee', padding: '6px 8px', fontSize: 12, fontFamily: 'ui-monospace, SF Mono, monospace' };
  const field = (key, title, hint) => (
    <div>
      <label style={labelStyle}>{title}</label>
      <input style={inputStyle} value={draft[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={hint || ''} />
    </div>
  );
  const msgColor = msg?.type === 'err' ? '#ef5350' : msg?.type === 'warn' ? '#ffd54f' : '#81c784';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <strong style={{ color: '#fff', fontSize: 13 }}>✏️ Edit config — {app.displayName || app.appName}</strong>
        <span style={{ fontSize: 11, color: '#888' }}>owner: {app.deployedBy}</span>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={{ ...styles.refreshBtn, padding: '3px 10px', fontSize: 11 }}>✕ Close</button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#eee', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!draft.forwardOktaToken} onChange={e => set('forwardOktaToken', e.target.checked)} />
        🔑 Forward my Okta sign-in to this app
        <span style={{ fontSize: 11, color: '#81c784' }}>(applies immediately, no redeploy)</span>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#eee', cursor: 'pointer' }}>
        <input type="checkbox" checked={!draft.ownerOnly} onChange={e => set('ownerOnly', !e.target.checked)} />
        🚀 Released to team {draft.ownerOnly ? <span style={{ color: '#ffb74d' }}>— 🔒 currently private to owner, co-admins, and named testers</span> : null}
        <span style={{ fontSize: 11, color: '#81c784' }}>(applies immediately, no redeploy)</span>
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {field('allowedGroups', 'Allowed Okta groups — comma-separated (blank = any signed-in user)', 'group-a, group-b')}
        {field('coAdmins', 'Co-admins — comma-separated emails (full management parity)', 'steve@example.com, jane@example.com')}
        {field('secretName', 'AWS secret name (blank = none)', 'my-app')}
        {field('branch', 'Branch', 'main')}
        {field('subdir', 'Subdirectory (blank = repo root)', 'apps/web')}
        {field('buildCommand', 'Build command', 'npm run build')}
        {field('startCommand', 'Start command', 'npm start')}
      </div>

      <div style={{ fontSize: 11, color: '#888' }}>
        <span style={{ color: '#81c784' }}>Applies live:</span> Okta forwarding, groups
        {'  ·  '}
        <span style={{ color: '#ffd54f' }}>Needs redeploy:</span> secret, branch, subdir, build/start command
      </div>

      {msg && <div style={{ fontSize: 12, color: msgColor }}>{msg.text}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onSave}
          disabled={saving}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: saving ? '#333' : '#1b3a2a', color: saving ? '#666' : '#81c784',
            border: '1px solid #81c784', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? '⏳ Saving…' : '💾 Save config'}
        </button>
        <button
          onClick={onRedeploy}
          disabled={saving}
          title="Stop, rebuild from repo, and respawn with the current config"
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: '#1a1f2a', color: '#4fc3f7',
            border: '1px solid #4fc3f7', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          🚀 Redeploy now
        </button>
      </div>
    </div>
  );
}

// Renders the deploy log for one app: every version that was deployed, newest
// first, with the action, timestamp, who did it, the branch, and the trigger.
// The history array is included in the /api/admin/apps response, so this is a
// pure presentational component (no fetch).
function DeployLogPanel({ app, oktaAuth }) {
  const history = Array.isArray(app.deployHistory) ? [...app.deployHistory].reverse() : [];
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const th = { textAlign: 'left', padding: '4px 8px', color: '#aaa', borderBottom: '1px solid #2a2a3a', fontWeight: 600, fontSize: 11 };
  const td = { padding: '4px 8px', borderBottom: '1px solid #1a1a26', fontSize: 11, color: '#cfd8dc', verticalAlign: 'top' };
  const actionColor = { initial: '#80cbc4', bump: '#81c784', set: '#ffb74d' };
  const fmt = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };
  // Pull the durable version report (S3 in prod / file in dev) and download it
  // as JSON. Falls back to live history server-side when no stored report exists.
  const downloadReport = async () => {
    setBusy(true); setMsg('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/apps/${encodeURIComponent(app.appName)}/version-report`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      const blob = new Blob([JSON.stringify(body.report, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${app.appName}-version-report.json`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      setMsg(body.source === 'store' ? `Stored report: ${body.storeLocation}` : 'Live history (no stored report yet)');
    } catch (e) {
      setMsg(`⚠️ ${e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#bbb', flexWrap: 'wrap' }}>
        <strong style={{ color: '#fff' }}>🕘 Deploy log for {app.displayName || app.appName}</strong>
        <span>Current: <code style={{ color: '#80cbc4' }}>v{app.version || '—'}</code></span>
        {fmtBuild(app.buildNumber) && <span>Build: <code style={{ color: '#80cbc4' }}>{fmtBuild(app.buildNumber)}</code></span>}
        <span>{history.length} deploy{history.length === 1 ? '' : 's'}</span>
        <button
          onClick={downloadReport}
          disabled={busy}
          style={{ padding: '3px 10px', fontSize: 11, fontWeight: 600, background: '#152238', color: '#7cc4ff', border: '1px solid #2f4a6a', borderRadius: 4, cursor: busy ? 'default' : 'pointer' }}
          title="Download the durable version report stored in S3 (JSON)"
        >
          {busy ? 'Fetching…' : '⬇ Download version report'}
        </button>
        {msg && <span style={{ fontSize: 11, color: msg.startsWith('⚠️') ? '#ef9a9a' : '#8a97a8' }}>{msg}</span>}
      </div>
      {history.length === 0 ? (
        <div style={{ color: '#666', fontSize: 12, fontStyle: 'italic' }}>
          No deploy history recorded yet. History is captured on the next deploy.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Version</th>
              <th style={th}>Build</th>
              <th style={th}>Action</th>
              <th style={th}>When</th>
              <th style={th}>By</th>
              <th style={th}>Branch</th>
              <th style={th}>Trigger</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h, i) => (
              <tr key={`${h.version}-${h.at}-${i}`}>
                <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', color: '#e0e0e0', fontWeight: 700 }}>v{h.version}</td>
                <td style={{ ...td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', color: '#9fb3c8' }}>{fmtBuild(h.build) || '—'}</td>
                <td style={{ ...td, color: actionColor[h.action] || '#cfd8dc', fontWeight: 600 }}>{h.action}</td>
                <td style={td}>{fmt(h.at)}</td>
                <td style={td}>{h.by || '—'}</td>
                <td style={td}>{h.branch || '—'}</td>
                <td style={td}>{h.trigger || '—'}{h.reason ? ` · ${h.reason}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Renders the response from GET /api/admin/apps/:appName/env. Two side-by-side
// tables: what's in the AWS secret (always available) and what the live
// process actually has (Linux/proc only). Values are masked server-side so
// this component just displays them as-is.
function EnvPanel({ env, loading, onRefresh }) {
  if (loading && !env) return <div style={{ color: '#aaa', fontSize: 12 }}>Loading…</div>;
  if (!env) return null;
  if (env._error) return <div style={styles.error}>⚠️ {env._error}</div>;

  const sec = env.secret || {};
  const proc = env.processEnv || {};
  const auth = env.auth || {};

  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 11 };
  const cellHeader = { textAlign: 'left', padding: '4px 8px', color: '#aaa', borderBottom: '1px solid #2a2a3a', fontWeight: 600 };
  const cellBody = { padding: '3px 8px', borderBottom: '1px solid #1a1a26', fontFamily: 'ui-monospace, SF Mono, monospace' };

  const badgeOn = { display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#1b3a1b', color: '#81c784', border: '1px solid #81c784' };
  const badgeOff = { display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: '#3a1a1a', color: '#ef5350', border: '1px solid #ef5350' };
  const noteRed = { marginTop: 8, padding: 8, background: '#2a1a1a', borderRadius: 4, fontSize: 11, color: '#ef9a9a' };
  const noteAmber = { marginTop: 8, padding: 8, background: '#332a17', borderRadius: 4, fontSize: 11, color: '#ffd54f' };

  const renderEntries = (entries, emptyMsg) => {
    if (!entries || entries.length === 0) {
      return <div style={{ color: '#666', fontSize: 11, fontStyle: 'italic' }}>{emptyMsg}</div>;
    }
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={cellHeader}>Key (masked)</th>
            <th style={cellHeader}>Value (last 4)</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.keyRaw}>
              <td style={{ ...cellBody, color: '#4fc3f7' }}>{e.keyMasked}</td>
              <td style={{ ...cellBody, color: e.hasValue ? '#c5e1a5' : '#999' }}>{e.valueMasked}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#bbb' }}>
        <strong style={{ color: '#fff' }}>🔑 Env vars for {env.appName}</strong>
        <span>PID: <code>{env.pid || 'n/a'}</code></span>
        <span>Status: <code>{env.status || 'n/a'}</code></span>
        <span style={{ flex: 1 }} />
        <button onClick={onRefresh} disabled={loading} style={{ ...styles.refreshBtn, padding: '3px 10px', fontSize: 11 }}>
          {loading ? '…' : '↻ Refresh env'}
        </button>
      </div>

      {/* Auth & session-forwarding diagnostics — the config behind "SESSION
          EXPIRED" reports. Surfaces the forwarded-token opt-in, whether it will
          survive a restart (persist-whitelist check), and live-vs-stored drift. */}
      <div style={{ background: '#15151f', padding: 12, borderRadius: 6 }}>
        <div style={{ color: '#ffb300', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
          🔐 Auth &amp; session forwarding
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', fontSize: 12, color: '#bbb' }}>
          <span>
            Forward Okta sign-in:{' '}
            <span style={auth.forwardOktaToken ? badgeOn : badgeOff}>
              {auth.forwardOktaToken ? '✅ ON' : '⛔ OFF'}
            </span>
          </span>
          <span>
            <code style={{ color: '#4fc3f7' }}>window.__LYRIC_OKTA_TOKEN__</code> injection:{' '}
            <code style={{ color: auth.tokenInjectionEnabled ? '#c5e1a5' : '#ef9a9a' }}>
              {auth.tokenInjectionEnabled ? 'active' : 'inactive'}
            </code>
            {auth.tokenInjectionEnabled && (
              <span style={{ color: '#888' }}>
                {' '}(+ live <code style={{ color: '#4fc3f7' }}>window.__lyricGetToken()</code>)
              </span>
            )}
          </span>
          <span>Owner: <code style={{ color: '#ccc' }}>{auth.owner || 'n/a'}</code></span>
          <span>Secret: <code style={{ color: '#ccc' }}>{auth.secretName || 'n/a'}</code></span>
          <span>
            Groups:{' '}
            <code style={{ color: '#ccc' }}>
              {auth.allowedGroups && auth.allowedGroups.length
                ? auth.allowedGroups.join(', ')
                : '(any signed-in user)'}
            </code>
          </span>
        </div>

        {/* Persistence check — the guard that catches the forwardOktaToken bug
            class: a field set in memory that the persist whitelist drops. */}
        {auth.wontPersist && auth.wontPersist.length > 0 ? (
          <div style={noteRed}>
            ⚠️ <strong>These auth fields will NOT survive a restart</strong> (dropped by the persist whitelist):{' '}
            <code>{auth.wontPersist.join(', ')}</code>. They reset on the next Lyric restart or auto-redeploy —
            add them to <code>PERSISTED_FIELDS</code> in <code>server/services/deployStateStore.js</code>.
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: 11, color: '#81c784' }}>
            ✅ Auth config will persist across restarts.
          </div>
        )}

        {/* Drift check — live (in-memory) differs from what's stored, so a
            restart/auto-redeploy would change behavior. Common right after a
            persist fix ships but before the app is re-deployed. */}
        {auth.persistDrift && auth.persistDrift.length > 0 && (
          <div style={noteAmber}>
            ⚠️ <strong>Live vs stored differ</strong> for: <code>{auth.persistDrift.join(', ')}</code>.
            A restart / auto-redeploy will apply the STORED value — redeploy the app to re-save.
            {auth.persisted && (
              <div style={{ marginTop: 4, fontFamily: 'ui-monospace, monospace' }}>
                forwardOktaToken — live: <b>{String(auth.live?.forwardOktaToken)}</b>, stored:{' '}
                <b>{String(auth.persisted?.forwardOktaToken)}</b>
              </div>
            )}
          </div>
        )}
        {auth.persistedError && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#ffb74d' }}>
            ℹ️ Couldn't read stored state to compare ({auth.persistedError}). Showing live values only.
          </div>
        )}
        {auth.backend && (
          <div style={{ marginTop: 6, fontSize: 10, color: '#666' }}>State backend: {auth.backend}</div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* AWS secret side — works everywhere */}
        <div style={{ background: '#15151f', padding: 12, borderRadius: 6 }}>
          <div style={{ color: '#ffb300', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            AWS Secret · <code style={{ color: '#bbb' }}>{sec.path}</code>
          </div>
          {sec.error && (
            <div style={{ color: '#ef5350', fontSize: 11, marginBottom: 8 }}>
              ⚠️ {sec.error}
            </div>
          )}
          {!sec.exists && !sec.error && (
            <div style={{ color: '#ffb74d', fontSize: 11, marginBottom: 8 }}>
              ⚠️ Secret not found at <code>{sec.path}</code> — Lyric had nothing to merge.
            </div>
          )}
          {renderEntries(sec.entries, 'No entries.')}
          <div style={{ marginTop: 6, fontSize: 10, color: '#666' }}>
            {sec.keyCount} key{sec.keyCount === 1 ? '' : 's'} in AWS Secrets Manager.
          </div>
        </div>

        {/* Live process env side — Linux only */}
        <div style={{ background: '#15151f', padding: 12, borderRadius: 6 }}>
          <div style={{ color: '#ffb300', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Live process env (matching secret keys only)
          </div>
          {!proc.ok && proc.osLimitation && (
            <div style={{ color: '#bbb', fontSize: 11, marginBottom: 8, fontStyle: 'italic' }}>
              ℹ️ {proc.osLimitation}
            </div>
          )}
          {!proc.ok && !proc.osLimitation && (
            <div style={{ color: '#ef5350', fontSize: 11, marginBottom: 8 }}>
              ⚠️ {proc.error || 'Process env unreadable'}
            </div>
          )}
          {proc.ok && renderEntries(proc.entries, 'No matching env vars in the process.')}
          {env.missing && env.missing.length > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#2a1a1a', borderRadius: 4, fontSize: 11, color: '#ef9a9a' }}>
              ⚠️ <strong>Missing from process env:</strong> {env.missing.join(', ')}
              <div style={{ marginTop: 4, color: '#bbb', fontStyle: 'italic' }}>
                These keys exist in the AWS secret but didn't reach the running process. Likely cause: app was started before the secret was set, or `secretName` on the app doesn't match the AWS path. Stop + Start to re-merge.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
